import { makeStyles } from "tss-react/mui"
import { Box, Stack, TextField } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons"

const BUTTON_SIZE = "2rem"

const useStyles = makeStyles()((theme) => ({
    base: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        height: BUTTON_SIZE,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        color: theme.palette.tailwind.slate[800],
        background: theme.palette.common.white,
    },
    button: {
        width: BUTTON_SIZE,
        cursor: "pointer",
        borderRadius: "0.625rem",
    },
    buttonDark: {
        background: theme.palette.tailwind.slate[800],
        borderColor: theme.palette.tailwind.slate[800],
        color: theme.palette.common.white,
    },
    countBox: {
        minWidth: BUTTON_SIZE,
        maxWidth: "3.5rem",
        color: theme.palette.tailwind.slate[800],
        background: theme.palette.common.white,
        "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: ".75rem",
        },
        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },

        "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        },
    },
    input: {
        height: BUTTON_SIZE,
        padding: "0 .25rem",
        textAlign: "center",
    },
}))

type PackageCounterProps = {
    count: number
    disableIncrease?: boolean
    handleCounter: (el: number) => void
}

export const ProductCounter = ({ count, handleCounter, disableIncrease = false }: PackageCounterProps) => {
    const { classes, cx } = useStyles()

    const handleIncrement = () => {
        handleCounter(count + 1)
    }

    const handleDecrement = () => {
        handleCounter(Math.max(count - 1, 0))
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "")
        const numericValue = value ? parseInt(value, 10) : 0

        if (disableIncrease && numericValue > count) {
            return
        }

        handleCounter(numericValue)
    }

    return (
        <Stack flexDirection="row" gap="0.25rem" alignItems="center">
            {count > 0 && (
                <>
                    <Box className={cx(classes.base, classes.button)} onClick={handleDecrement}>
                        <FontAwesomeIcon icon={faMinus} />
                    </Box>
                    <TextField
                        type="text"
                        value={count}
                        onChange={handleInputChange}
                        size="small"
                        inputProps={{
                            className: classes.input,
                            maxLength: 4,
                        }}
                        className={classes.countBox}
                    />
                </>
            )}
            {!disableIncrease && (
                <Box
                    className={cx(classes.base, classes.button, count === 0 && classes.buttonDark)}
                    onClick={handleIncrement}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </Box>
            )}
        </Stack>
    )
}
